import JsonNode from "@/components/common/shared/JSONViewer/JsonNode"
import useSnackbar from "@/helpers/add-snackbar"
import {
  FileCopy,
  KeyboardArrowDown,
  KeyboardArrowRight
} from "@material-ui/icons"
import React, { useEffect, useRef, useState } from "react"
import { useStyles } from "./JsonViewerWithCopy"

const buildSimplePath = (path: string[]): string => {
  return path.join(".")
}

const buildCustomPath = (path: string[], value: any): string => {
  let formattedPath = path.reduce((acc, segment, i, arr) => {
    const isArrayIndex = !isNaN(Number(segment))
    if (
      isArrayIndex &&
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      const conditions = Object.entries(value)
        .filter(([key]) => typeof key === "string" && key !== "__proto__")
        .map(([key, val]) => `${key}='${val}'`)
        .join(",")
      return `${acc}[${conditions}]`
    }

    const separator = i === 0 ? "" : segment.startsWith("[") ? "" : "."

    if (i === arr.length - 1) {
      return `${acc}${separator}${segment}`
    }
    return `${acc}${separator}${segment}`
  }, "")

  return `{${formattedPath}}`
}

type Props = {
  data: object | null | undefined
}

const JSONNodeDisplay = ({ data }: Props) => {
  const { addSnackbar } = useSnackbar()
  const classes = useStyles()
  const [collapsed, setCollapsed] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState("")
  const contentRef = useRef<HTMLDivElement>(null)
  const [showScrollButtons, setShowScrollButtons] = useState(false)

  const scrollByAmount = (amount: number) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({ left: amount, behavior: "smooth" })
    }
  }

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const checkOverflow = () => {
      setShowScrollButtons(el.scrollWidth > el.clientWidth + 1)
    }

    const rafId = window.requestAnimationFrame(checkOverflow)
    window.addEventListener("resize", checkOverflow)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener("resize", checkOverflow)
    }
  }, [data, collapsed, searchQuery])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    addSnackbar("JSON copied to clipboard")
  }
  const handleCopyValue = (value: any) => {
    const jsonValue = JSON.stringify(value, null, 2)
    navigator.clipboard.writeText(jsonValue)
    addSnackbar(`Copied value: ${jsonValue}`)
  }

  const clean_t_strings_from_path = (path: string[]) => {
    const cleaned_path: string[] = []

    for (const segment of path) {
      // Remove patterns like "t=20", "t=9999", "[t=20]", etc.
      const cleaned = segment.replace(/t=\d+/g, "").replace(/\[\]/g, "[]")

      cleaned_path.push(cleaned)
    }

    return cleaned_path
  }

  const handleCopyPath = (path: string[]) => {
    const cleaned_path = clean_t_strings_from_path(path)
    const simplePath = buildSimplePath(cleaned_path)
    navigator.clipboard.writeText(simplePath)
    addSnackbar(`Copied path: ${simplePath}`)
  }

  const handleCopyFormatted = (path: string[], value: any) => {
    const cleaned_path = clean_t_strings_from_path(path)
    const formatted = buildCustomPath(cleaned_path, value)
    navigator.clipboard.writeText(formatted)
    addSnackbar(`Copied formatted: ${formatted}`)
  }

  const handleCopyFormattedMultiArray = (path: string[], value: any) => {
    const total = path.find(p => p.includes("t="))
    const total_index = path.findIndex(p => p.includes("t="))
    const match = total?.match(/t=(\d+)/)
    const total_number_value = match ? parseInt(match[1], 10) : -1

    const cleaned_path = clean_t_strings_from_path(path)

    // build new paths
    const new_paths_with_i_numbers: string[] = []
    for (let i = 0; i < total_number_value; i++) {
      const newPath = [...cleaned_path]
      newPath[total_index] = `[i=${i}]`
      new_paths_with_i_numbers.push(buildCustomPath(newPath, value))
    }

    const formatted = new_paths_with_i_numbers.join("\n")
    navigator.clipboard.writeText(formatted)
    addSnackbar(`Copied formatted: ${formatted}`)
  }

  const renderValue = (value: any): React.ReactNode => {
    if (typeof value !== "string") {
      return String(value)
    }
    if (!searchQuery) {
      return value
    }
    const regex = new RegExp(
      `(${searchQuery
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\s+/g, "\\s+")})`,
      "gi"
    )

    const parts = value.split(regex)
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === searchQuery.toLowerCase() ? (
            <span
              key={i}
              style={{
                backgroundColor: "#fff3cd",
                padding: "0 2px",
                borderRadius: "2px",
                display: "inline-block",
                margin: "-1px 0"
              }}
            >
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    )
  }

  return (
    <div>
      {data && Object.keys(data).length > 0 && (
        <div className={classes.viewerSection}>
          <div className={classes.viewerHeader}>
            <div className={classes.searchBar}>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search in JSON..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
            <div className={classes.headerButtonGroup}>
              <button
                type="button"
                onClick={handleCopyJson}
                className={`${classes.headerButton} ${classes.copyButton}`}
                title="Copy JSON"
              >
                <FileCopy fontSize="small" />
                Copy
              </button>
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className={`${classes.headerButton} ${classes.collapseButton}`}
                title={collapsed ? "Expand All" : "Collapse All"}
              >
                {collapsed ? (
                  <KeyboardArrowRight fontSize="small" />
                ) : (
                  <KeyboardArrowDown fontSize="small" />
                )}
                {collapsed ? "Expand" : "Collapse"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: "8px"
            }}
          >
            {showScrollButtons && (
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollByAmount(-240)}
                style={{
                  minWidth: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed rgba(0,0,0,0.2)",
                  background: "rgba(0,0,0,0.07)"
                }}
              />
            )}

            <div
              ref={contentRef}
              className={classes.viewerContent}
              style={{
                maxWidth: "100%",
                overflowX: "auto",
                flex: 1
              }}
            >
              <JsonNode
                data={data}
                path={[]}
                onCopyValue={handleCopyValue}
                onCopyPath={handleCopyPath}
                onCopyFormatted={handleCopyFormatted}
                onCopyFormattedMultiArray={handleCopyFormattedMultiArray}
                collapsed={collapsed}
                renderValue={renderValue}
                searchQuery={searchQuery}
              />
            </div>

            {showScrollButtons && (
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollByAmount(240)}
                style={{
                  minWidth: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed rgba(0,0,0,0.2)",
                  background: "rgba(0,0,0,0.07)"
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default JSONNodeDisplay
